// Shared color scale + label rules for the soil moisture views
// (CustomSentosaInsightDashboard heatmap card and SoilMoistureDashboard).
// Kept in one place so the two dashboards can't drift apart again.

// Sequential single-hue ramp for soil moisture magnitude (low -> high).
// Validated against the dark glass card surface: monotone lightness,
// adjacent dL >= 0.06, dark end 2.71:1 vs surface, hue spread 3 deg.
export const MOISTURE_RAMP = [
  '#256abf',
  '#3987e5',
  '#6da7ec',
  '#9ec5f4',
  '#cde2fb',
]
export const MOISTURE_MIN = 10
export const MOISTURE_MAX = 40

// Status palette — reserved for state, never reused as a series color
export const STATUS_GOOD = '#0ca30c'
export const STATUS_WARNING = '#fab219'
export const STATUS_SERIOUS = '#ec835a'
export const STATUS_CRITICAL = '#d03b3b'
export const OVER_SUPPLIED = '#3B82F6'

export const NO_DATA_INK = 'rgba(226,232,240,0.55)'
// Empty cells recede into the surface instead of competing with real readings
export const NO_DATA_FILL =
  'repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 2px, transparent 2px 6px)'

const clamp01 = (x: number) => Math.max(0, Math.min(1, x))

export const moistureStep = (v: number) => {
  const t = clamp01((v - MOISTURE_MIN) / (MOISTURE_MAX - MOISTURE_MIN))
  return Math.min(MOISTURE_RAMP.length - 1, Math.floor(t * MOISTURE_RAMP.length))
}

export const colorForMoisture = (v?: number, fallback = 'transparent') =>
  v == null ? fallback : MOISTURE_RAMP[moistureStep(v)]

// The top three ramp steps are too light to carry white text
export const inkForMoisture = (v?: number) =>
  v == null ? NO_DATA_INK : moistureStep(v) >= 2 ? '#0b1220' : '#ffffff'

export const colorForWaterBalance = (v?: number, fallback = 'transparent') => {
  if (typeof v !== 'number') return fallback
  if (v > 1) return OVER_SUPPLIED
  if (v >= 0.5) return STATUS_GOOD
  if (v >= 0.25) return STATUS_WARNING
  return STATUS_CRITICAL
}

export const colorForTemp = (v?: number, fallback = 'transparent') => {
  if (v == null) return fallback
  return v > 33 ? STATUS_WARNING : STATUS_GOOD
}

// Oxygen thresholds are expressed in percent
export const colorForOxygenPercent = (v?: number, fallback = 'transparent') => {
  if (v == null) return fallback
  if (v > 18) return STATUS_GOOD
  if (v >= 15) return '#4ADE80'
  if (v >= 12) return STATUS_WARNING
  if (v >= 8) return STATUS_SERIOUS
  if (v >= 5) return STATUS_CRITICAL
  return '#7C3AED'
}

// Strips admin cruft from raw asset names:
//   "Serapong G 4 front (new2/11/25)" -> "G4 front (new)"
//   "Serapong G 5 --O2 -6/6/25"       -> "G5 O2"
// The "(new)" and "O2" markers are kept on purpose — without them sensors on
// the same green collapse to identical labels.
export const cleanSensorName = (raw: string) =>
  raw
    .replace(/Serapong\s*/i, '')
    .replace(/_/g, ' ')
    .replace(/-?\d{1,2}\/\d{1,2}\/\d{2,4}/g, '')
    .replace(/--\s*/g, ' ')
    .replace(/\)+/g, ')')
    .replace(/\(\s*\)/g, '')
    .replace(/\s*\(\s*/g, ' (')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^G\s+(\d)/i, 'G$1')
